"use client";

import { Home, LayoutGrid, ShoppingCart, ShoppingBag, User, LogIn, ChevronDown } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useApi } from "@/src/api/useApi";
import { CategoriesAllType } from "@/src/types/cat";
import useCartStore from "@/src/store/useCartStore";
import useUserStore from "@/src/store/useUser";
import useLoginStore from "@/src/store/useLoginStore";
import { PersianNum } from "@/src/tools";

const MobileDock = () => {
  const pathname = usePathname();
  const { dataUser } = useUserStore();
  const { openLogin } = useLoginStore();
  const cart = useCartStore((state) => state.cart);
  
  const { data: catUrlData } = useApi<CategoriesAllType>("/public/categories", {
    revalidateOnFocus: false,
  });

  const rootCats = useMemo(() => catUrlData?.data ?? [], [catUrlData?.data]);
  const [activeRootId, setActiveRootId] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [openChildId, setOpenChildId] = useState<string | null>(null);

  const activeRoot = useMemo(() => {
    if (!rootCats.length) return null;
    return rootCats.find((root) => root.id === activeRootId) ?? rootCats[0];
  }, [activeRootId, rootCats]);


  // محاسبه تعداد آیتم‌های سبد خرید
  const itemCount = cart?.items?.reduce((total, item) => total + item.quantity, 0) ?? 0;

  // بررسی اینکه آیا در صفحه اصلی هستیم
  const isHome = pathname === "/";
  const isShop = pathname?.startsWith("/product-category") || pathname?.startsWith("/product");
  const isCart = pathname?.startsWith("/order");
  const isProfile = pathname?.startsWith("/profile");

  // لینک «فروشگاه» — اولین دسته‌ی root (در صورت نبود، خانه)
  const shopHref =
    rootCats?.[0]?.cat_url ? `/product-category/${rootCats[0].cat_url}` : "/";

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
      <div className="flex items-center justify-around h-16 px-2">
        {/* خانه (راست) */}
        <Link
          href="/"
          className={`flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
            isHome ? "text-blue-600" : "text-gray-600"
          }`}
        >
          <Home className="w-5 h-5" />
          <span className="text-[10px] font-medium">خانه</span>
        </Link>

        {/* فروشگاه */}
        <Link
          href={shopHref}
          className={`flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
            isShop ? "text-blue-600" : "text-gray-600"
          }`}
        >
          <ShoppingBag className="w-5 h-5" />
          <span className="text-[10px] font-medium">فروشگاه</span>
        </Link>

        {/* سبد خرید (وسط) */}
        <Link
          href="/order/check"
          className={`relative flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
            isCart ? "text-blue-600" : "text-gray-600"
          }`}
        >
          <div className="relative">
            <ShoppingCart className="w-6 h-6" />
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white shadow-lg">
                {PersianNum(itemCount)}
              </span>
            )}
          </div>
          <span className="text-[10px] font-medium">سبد خرید</span>
        </Link>

        {/* دسته‌بندی */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              className={`flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
                sheetOpen ? "text-blue-600" : "text-gray-600"
              }`}
            >
              <LayoutGrid className="w-5 h-5" />
              <span className="text-[10px] font-medium">دسته‌بندی</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="h-[85vh] max-h-[700px] rounded-t-3xl border-t border-gray-200 p-0 overflow-hidden"
            dir="rtl"
          >
            <SheetHeader className="px-4 pt-4 pb-3 border-b border-gray-100 flex flex-row items-center justify-end">
              <SheetTitle className="text-base font-bold text-left text-gray-900">همه دسته بندی ها</SheetTitle>
            
            </SheetHeader>
            <div className="flex h-[calc(100%-65px)] overflow-hidden">
              {/* ستون سمت راست: لیست دسته‌های مادر */}
              <div className="w-[120px] bg-gray-50 py-3 px-2 flex flex-col gap-0.5 overflow-y-auto border-l border-gray-200">
                {rootCats.map((root) => {
                  const isActive = activeRoot?.id === root.id;
                  if (!root.show_in_menu) return null;
                  return (
                    <button
                      key={root.id}
                      onClick={() => setActiveRootId(root.id)}
                      className={`
                        cursor-pointer text-right px-2 py-2.5 rounded-lg transition-all duration-200 text-xs
                        ${isActive
                          ? "bg-white text-blue-600 font-semibold shadow-sm"
                          : "text-gray-700 hover:bg-white/60"}
                      `}
                    >
                      <span className="line-clamp-2 leading-tight">{root.cat_title}</span>
                    </button>
                  );
                })}
              </div>

              {/* ستون سمت چپ: محتوای زیرمجموعه */}
              <div className="flex-1 bg-white overflow-y-auto">
                {activeRoot ? (
                  <div className="p-4">
                    {/* هدر */}
                    <div className="mb-4">
                      <Link
                        href={`/product-category/${activeRoot.cat_url}`}
                        onClick={() => setSheetOpen(false)}
                        className="text-sm text-blue-600 font-medium hover:underline inline-flex items-center gap-1"
                      >
                        همه محصولات {activeRoot.cat_title}
                        <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                      </Link>
                    </div>

                    {/* لیست بچه‌ها با آکاردیون */}
                    {activeRoot.children && activeRoot.children.length > 0 ? (
                      <div className="w-full space-y-0">
                        {activeRoot.children.map((child) => {
                          if (!child.show_in_menu) return null;
                          
                          // حذف تکرار ویژگی‌ها بر اساس id
                          const allAttributes = child.Properties?.flatMap((prop) => prop.Attributes || []) || [];
                          const uniqueAttributes = Array.from(
                            new Map(allAttributes.map((attr) => [attr.id, attr])).values()
                          );
                          const hasAttributes = uniqueAttributes.length > 0;
                          const isOpen = openChildId === `child-${child.id}`;
                          
                          return (
                            <div
                              key={child.id}
                              className="border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-center gap-2">
                                <Link
                                  href={`/product-category/${child.cat_url}`}
                                  onClick={() => setSheetOpen(false)}
                                  className="flex-1 py-3 text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors"
                                >
                                  {child.cat_title}
                                </Link>
                                {hasAttributes && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setOpenChildId(isOpen ? null : `child-${child.id}`);
                                    }}
                                    className="py-3 px-2 flex-shrink-0 hover:bg-gray-50 rounded-lg transition-colors"
                                  >
                                    <ChevronDown 
                                      className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                                    />
                                  </button>
                                )}
                              </div>
                              {hasAttributes && isOpen && (
                                <div className="pb-3 px-0 animate-in slide-in-from-top-2 duration-200">
                                  <div className="flex flex-wrap gap-2 pr-4">
                                    {uniqueAttributes.map((attr) => (
                                      <Link
                                        key={attr.id}
                                        href={`/product-category/${child.cat_url}?attr=[${attr.id}]`}
                                        onClick={() => setSheetOpen(false)}
                                        className="inline-flex flex-col gap-1 px-3 py-2 rounded-xl bg-gray-50 hover:bg-blue-50 text-xs text-gray-800 hover:text-blue-700 transition-all active:scale-95 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
                                      >
                                        <span className="font-semibold leading-tight">{attr.attribute_name}</span>
                                        {attr.attribute_price && (
                                          <span className="text-[10px] font-bold text-gray-600 whitespace-nowrap">
                                            {PersianNum(attr.attribute_price)} تومان
                                          </span>
                                        )}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                        <p className="text-xs">زیرمجموعه‌ای یافت نشد.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <span className="loading loading-spinner text-blue-500 loading-sm"></span>
                  </div>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* ورود/پروفایل */}
        {dataUser?.token ? (
          <Link
            href="/profile"
            className={`flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
              isProfile ? "text-blue-600" : "text-gray-600"
            }`}
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] font-medium">پروفایل</span>
          </Link>
        ) : (
          <button
            onClick={openLogin}
            className="flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors text-gray-600"
          >
            <LogIn className="w-5 h-5" />
            <span className="text-[10px] font-medium">ورود</span>
          </button>
        )}
      </div>
    </nav>
  );
};

export default MobileDock;

